import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { apiService } from "../lib/api"
import { config } from "../lib/config"

// ---- Helpers ---- //
function base64UrlDecode(base64Url: string): string {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
  const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=")

  if (typeof window === "undefined") {
    return Buffer.from(padded, "base64").toString("utf-8")
  }
  return atob(padded)
}

function parseJwt<T = any>(token: string): T | null {
  try {
    const [, payload] = token.split(".")
    return JSON.parse(base64UrlDecode(payload))
  } catch {
    return null
  }
}

function isTokenExpired(token: string, bufferMinutes: number = 5): boolean {
  const payload = parseJwt<JwtPayload>(token)
  if (!payload?.exp) return true
  
  const now = Date.now() / 1000
  return (payload.exp - now) < (bufferMinutes * 60)
}

// ---- Types ---- //
interface JwtPayload {
  sub: string
  roles?: string[]
  tenantId: string
  exp?: number
  [key: string]: any
}

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  companyName: string
  role: "super_admin" | "admin" | "auditor" | "employee" | "guest" | "manager" | "waiter" | "receptionist"
  isEmailVerified: boolean
  createdAt: string
  tenantId: string
  phone?: string
  country?: string
  passportNumber?: string
  idNumber?: string
  accessScope?: string[]
}

interface RegisterData {
  firstName: string
  lastName: string
  email: string
  companyName: string
  phone: string
  password: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isRefreshing: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  updateUser: (userData: Partial<User>) => Promise<void>
  refreshTokens: () => Promise<boolean>
  refreshUser: () => Promise<void>
  ensureValidToken: () => Promise<boolean>
  handleAuthError: () => Promise<boolean>
  clearAllData: () => void
}

// ---- Context ---- //
const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [mounted, setMounted] = useState(false)
  const qc = useQueryClient()
  const navigate = useNavigate()

  // Component mounted
  useEffect(() => {
    // Component ready
  }, [mounted])

  // Ensure a company id is present in storage after login/refresh
  const ensureCompanyId = async (payload?: JwtPayload) => {
    try {
      // Skip company setup for guests
      const userRole = payload?.roles?.[0] || user?.role
      if (userRole === 'guest') {
        return
      }

      let existing = localStorage.getItem("company_id") || localStorage.getItem("companyId") || localStorage.getItem("company")
      
      // Clear invalid 'guest-company' if present
      if (existing === 'guest-company') {
        localStorage.removeItem("company_id");
        localStorage.removeItem("companyId");
        localStorage.removeItem("company");
        existing = null;
      }
      
      // Wait a bit to ensure auth headers are properly set
      await new Promise(resolve => setTimeout(resolve, 200))
      
      let list: any[] = [];
      
      try {
        // First try standard companies endpoint
        const companies = await apiService.getCompanies({ page: 1, pageSize: 100 }) as any
        list = (companies?.data as any[]) || (companies as any[]) || []
      } catch (err) {
        console.warn('getCompanies failed, trying getMyCompanies', err);
        // Fallback to my-companies endpoint which seems to work for users
        try {
          const myCompanies = await apiService.getMyCompanies();
          // Map structure from UserCompany to Company
          list = myCompanies.map((uc: any) => uc.company || uc);
        } catch (e) {
          console.error('Both company fetch methods failed', e);
        }
      }

      // Filter out inactive companies
      list = list.filter((company: any) => {
        const status = company.status || 'active';
        return status !== 'inactive' && status !== 'INACTIVE';
      });
      const first = Array.isArray(list) ? list[0] : undefined
      
      if (first?.id) {
        if (!existing || list.length === 1 || !list.find((c: any) => c.id === existing)) {
          console.log(`[Auth] Setting default company to ${first.id} (${first.name})`);
          localStorage.setItem("company_id", first.id)
          if (first.name) localStorage.setItem("company_name", first.name)
          // Trigger role update if needed
          window.dispatchEvent(new CustomEvent('companyChanged', { detail: { companyId: first.id } }));
        }
      }
    } catch (error) {
       console.log('Failed to fetch companies for company ID setup:', error)
    }
  }

  // ---- Token Refresh ---- //
  const refreshTokens = async (): Promise<boolean> => {
    // Only run on client side
    if (typeof window === "undefined") {
      return false
    }

    if (isRefreshing) {
      // Prevent multiple simultaneous refresh attempts
      return false
    }

    const rt = localStorage.getItem("refresh_token")
    const tenantId = localStorage.getItem("tenant_id")
    
    if (!rt || !tenantId) {
      // No refresh token available, log out
      console.warn("No refresh token available, logging out")
      await logout()
      return false
    }

    setIsRefreshing(true)
    
    try {
      const { accessToken } = await apiService.refresh(rt)

      const payload = parseJwt<JwtPayload>(accessToken)
      if (!payload) throw new Error("Invalid JWT received on refresh")

      // Update tokens in storage and API service
      localStorage.setItem("auth_token", accessToken)
      localStorage.setItem("tenant_id", payload.tenantId)

      apiService.setAuth(accessToken, payload.tenantId)
      apiService.setRefreshToken(rt) // Keep the same refresh token

      await ensureCompanyId(payload)

      // Update user state
      const companyName = localStorage.getItem('company_name') || 'My Company'
      const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId')
      
      let userRole = payload.roles?.[0] || "employee";
      
      // If we have a selected company, try to get the role for that company
      if (companyId) {
        try {
          const myCompanies = await apiService.getMyCompanies();
          const currentCompanyAccess = myCompanies.find((c: any) => c.companyId === companyId || c.company?.id === companyId);
          if (currentCompanyAccess && currentCompanyAccess.role) {
            userRole = currentCompanyAccess.role;
            console.log(`[Auth] Switched to company role: ${userRole} for company ${companyId}`);
          }
          
          // Also set access scope if available
          if (currentCompanyAccess && (currentCompanyAccess as any).accessScope) {
             (payload as any).accessScope = (currentCompanyAccess as any).accessScope;
          }
        } catch (e) {
          console.warn('Failed to fetch company specific role', e);
        }
      }

      setUser({
        id: payload.sub,
        email: payload.sub,
        firstName: "User",
        lastName: "",
        companyName,
        role: userRole as User["role"],
        isEmailVerified: true,
        createdAt: new Date().toISOString(),
        tenantId: payload.tenantId,
        accessScope: (payload as any).accessScope
      })

      return true
    } catch (err: any) {
      console.error("Token refresh failed", err)
      
      // Check if error is due to expired or invalid token
      const errorMessage = err?.message || err?.error?.message || ''
      const isTokenError = errorMessage.includes('expired') || 
                          errorMessage.includes('invalid') || 
                          errorMessage.includes('Refresh token') ||
                          err?.status === 401
      
      if (isTokenError) {
        console.warn("Token expired or invalid, logging out user")
        await logout()
      }
      
      return false
    } finally {
      setIsRefreshing(false)
    }
  }

  // Check and refresh token if needed before API calls
  const ensureValidToken = async (): Promise<boolean> => {
    // Only run on client side
    if (typeof window === "undefined") {
      return false
    }

    const token = localStorage.getItem("auth_token")
    if (!token) return false

    if (isTokenExpired(token)) {
      return await refreshTokens()
    }

    return true
  }

  // Handle 401 errors by attempting token refresh
  const handleAuthError = async (): Promise<boolean> => {
    const success = await refreshTokens()
    
    if (!success) {
      await logout()
    }
    
    return success
  }

  // ---- Auth Status ---- //
  const checkAuthStatus = async () => {
    // Only run on client side
    if (typeof window === "undefined") {
      setIsLoading(false)
      return
    }

    // Don't check auth status on public auth pages (login, forgot password, etc.) or public pages (restaurant menu, accommodation, etc.)
    const publicAuthPaths = [
      '/login', 
      '/auth/login', 
      '/auth/forgot-password', 
      '/verify-email', 
      '/unauthorized',
      '/restaurant-menu',  // Public restaurant menu
      '/accommodation',    // Public accommodation page
      '/bar-and-grill',    // Public bar and grill page
      '/tours',            // Public tours page
      '/contact',          // Public contact page
      '/room/',            // Public room details pages
    ]
    const currentPath = window.location.pathname
    if (publicAuthPaths.some(path => currentPath === path || currentPath.startsWith(path))) {
      setIsLoading(false)
      return
    }

    try {
      const token = localStorage.getItem("auth_token")
      const tenantId = localStorage.getItem("tenant_id")
      
      if (!token || !tenantId) {
        await logout()
        return
      }

      const payload = parseJwt<JwtPayload>(token)
      if (!payload) {
        await logout()
        return
      }

      // Check if token is expired or will expire soon (within 5 minutes)
      const now = Date.now() / 1000
      const expiresSoon = payload.exp && (payload.exp - now) < 300 // 5 minutes

      if (payload.exp && (now >= payload.exp || expiresSoon)) {
        console.warn("Access token expired or expiring soon, attempting refresh")
        const refreshSuccess = await refreshTokens()
        
        if (!refreshSuccess) {
          console.warn("Token refresh failed, logging out")
          await logout()
        }
        return
      }

        apiService.setAuth(token, tenantId)
        // Ensure refresh token is available for 401 auto-refresh on first load
        try {
          const rt = localStorage.getItem("refresh_token")
          if (rt) apiService.setRefreshToken(rt)
        } catch {}
        // Add a small delay to ensure auth headers are properly set
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Try to ensure company ID, but don't fail auth if this errors
        try {
          await ensureCompanyId(payload)
        } catch (error) {
          // Silently fail - company ID setup errors shouldn't prevent authentication
          console.warn("Company ID setup failed, but continuing with auth:", error)
        }
        
      // Fetch user profile from API to get the actual name
      let userProfile = null
      try {
        userProfile = await apiService.getCurrentUser()
      } catch (error) {
        // Silently fail - will use defaults
        console.warn("Failed to fetch user profile, using defaults:", error)
      }
      
      // Extract name from user profile or use defaults
      const userName = userProfile?.name || (payload as any).name || null
      const userEmail = userProfile?.email || payload.sub
      let userRole = userProfile?.role || payload.roles?.[0] || "employee"
      
      let accessScope: string[] | undefined;
      // If we have a selected company, try to get the role for that company
      const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId')
      if (companyId) {
        try {
          // We need to fetch my companies to find the role for this specific company
          // This might be redundant if we just fetched user profile, but user profile (auth/me) typically returns global role
          const myCompanies = await apiService.getMyCompanies();
          const currentCompanyAccess = myCompanies.find((c: any) => c.companyId === companyId || c.company?.id === companyId);
          console.log('[Auth Debug] My Companies:', myCompanies);
          console.log('[Auth Debug] Selected Company ID:', companyId);
          console.log('[Auth Debug] Found Access:', currentCompanyAccess);
          
          if (currentCompanyAccess && currentCompanyAccess.role) {
            userRole = currentCompanyAccess.role;
            console.log(`[Auth check] Switched to company role: ${userRole} for company ${companyId}`);
          }
          
          console.log('[Auth check] currentCompanyAccess details:', {
            role: currentCompanyAccess?.role,
            accessScope: (currentCompanyAccess as any)?.accessScope,
            permissions: (currentCompanyAccess as any)?.permissions
          });
          
          // Also set access scope if available
          if (currentCompanyAccess && (currentCompanyAccess as any).accessScope) {
             accessScope = (currentCompanyAccess as any).accessScope;
          }
        } catch (e) {
          console.warn('Failed to fetch company specific role during auth check', e);
        }
      }

      const emailVerified = userProfile?.emailVerified ?? true // Default to true for backward compatibility
      const userPhone = userProfile?.phone || null
      const userCountry = (userProfile as any)?.country || null
      const userPassportNumber = (userProfile as any)?.passportNumber || null
      const userIdNumber = (userProfile as any)?.idNumber || null
      
      // Use firstName and lastName from profile if available, otherwise split name
      let firstName = userProfile?.firstName || "User"
      let lastName = userProfile?.lastName || ""
      if (!userProfile?.firstName && userName) {
        const nameParts = userName.trim().split(' ')
        if (nameParts.length >= 2) {
          firstName = nameParts[0]
          lastName = nameParts.slice(1).join(' ')
        } else {
          firstName = nameParts[0]
        }
      }
      
      const userData = {
            id: payload.sub,
        email: userEmail,
        firstName,
        lastName,
        companyName: localStorage.getItem('company_name') || 'My Company',
        role: userRole as User["role"],
            isEmailVerified: emailVerified,
            createdAt: userProfile?.createdAt || new Date().toISOString(),
        tenantId,
        phone: userPhone || undefined,
        country: userCountry || undefined,
        passportNumber: userPassportNumber || undefined,
        idNumber: userIdNumber || undefined,
        // Also store the full name for easy access
        ...(userName && { fullName: userName, name: userName }),
        accessScope: accessScope || (userProfile as any)?.accessScope
      }
      
      setUser(userData as User)
    } catch (error) {
      console.error("Auth check failed", error)
      // Only logout if token is actually invalid or missing
      // Don't logout for network errors or API errors if token is valid
      const token = localStorage.getItem("auth_token")
      if (!token) {
        await logout()
      } else {
        // Token exists but something else failed - try to set user from token payload
        try {
          const payload = parseJwt<JwtPayload>(token)
          if (payload) {
            const userData = {
              id: payload.sub,
              email: payload.sub,
              firstName: "User",
              lastName: "",
              companyName: localStorage.getItem('company_name') || 'My Company',
              role: (payload.roles?.[0] || "employee") as User["role"],
              isEmailVerified: true,
              createdAt: new Date().toISOString(),
              tenantId: payload.tenantId || localStorage.getItem("tenant_id") || ""
            }
            setUser(userData as User)
          } else {
            await logout()
          }
        } catch (fallbackError) {
          console.error("Fallback auth setup failed", fallbackError)
          await logout()
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ---- Login ---- //
  const login = async (email: string, password: string) => {
    setIsLoading(true)
    try {
      try { if (typeof window !== 'undefined') localStorage.removeItem('disable_demo_auth') } catch {}
      
      // Attempt MFA-aware login
      const result = await apiService.loginMfa(email, password)
      if (!result.ok) {
        // Show prompt for MFA code
        const code = typeof window !== 'undefined' ? window.prompt('Enter your MFA code or a backup code:') : ''
        if (!code) throw new Error('MFA code is required')
        const tokens = await apiService.verifyMfaLogin((result as { ok: false; challengeToken: string }).challengeToken, code)
        const payload = parseJwt<JwtPayload>(tokens.accessToken)
        if (!payload) throw new Error('Invalid JWT received')
        const tenantId = payload.tenantId || "tenant_default"
        try { localStorage.setItem("auth_token", tokens.accessToken) } catch {}
        try { if (tokens.refreshToken) localStorage.setItem("refresh_token", tokens.refreshToken) } catch {}
        try { localStorage.setItem("tenant_id", tenantId) } catch {}
        apiService.setAuth(tokens.accessToken, tenantId)
        if (tokens.refreshToken) apiService.setRefreshToken(tokens.refreshToken)
        await ensureCompanyId(payload)
        
        // Fetch user profile to get emailVerified status
        let userProfile = null
        try {
          userProfile = await apiService.getCurrentUser()
        } catch (error) {
          // Silently fail - will use defaults
        }
        
        setUser({
          id: payload.sub,
          email,
          firstName: "User",
          lastName: "",
          companyName: localStorage.getItem('company_name') || 'My Company',
          role: payload.roles?.[0] as User["role"] || "employee",
          isEmailVerified: userProfile?.emailVerified ?? true,
          createdAt: new Date().toISOString(),
          tenantId
        })
        return
      }

      const loginResp = result.tokens

      const accessToken = (loginResp as any).accessToken ?? (loginResp as any).access_token ?? (loginResp as any).token ?? (loginResp as any).data?.accessToken
      const refreshToken = (loginResp as any).refreshToken ?? (loginResp as any).refresh_token ?? (loginResp as any).data?.refreshToken ?? (loginResp as any).refreshToken


      if (!accessToken || typeof accessToken !== 'string') {
        console.error('Login response missing access token or token is not a string', loginResp)
        throw new Error('Invalid JWT received')
      }

      // Quick sanity check for JWT structure
      if (!accessToken.includes('.') || accessToken.split('.').length !== 3) {
        console.error('Access token does not look like a JWT (missing dot separators)', accessToken)
        throw new Error('Invalid JWT received')
      }

      const payload = parseJwt<JwtPayload>(accessToken)
      if (!payload) {
        console.error('Failed to parse JWT payload for token', accessToken.substring(0, 32))
        throw new Error('Invalid JWT received')
      }

      const tenantId = payload.tenantId || "tenant_default"

  try { localStorage.setItem("auth_token", accessToken) } catch {}
  try { if (refreshToken) localStorage.setItem("refresh_token", refreshToken) } catch {}
  try { localStorage.setItem("tenant_id", tenantId) } catch {}

  apiService.setAuth(accessToken, tenantId)
  if (refreshToken) apiService.setRefreshToken(refreshToken)

      await ensureCompanyId(payload)

      // Get company name from localStorage or use a default
      const companyName = localStorage.getItem('company_name') || 'My Company'
      
      // Fetch user profile from API to get the actual name
      let userProfile = null
      try {
        userProfile = await apiService.getCurrentUser()
      } catch (error) {
        // Silently fail - will use defaults
      }
      
      // Extract name from user profile or use defaults
      const userName = userProfile?.name || (payload as any).name || null
      const userEmail = userProfile?.email || email
      let userRole = userProfile?.role || payload.roles?.[0] || "employee"
      
      let accessScope: string[] | undefined;
      // If we have a selected company, try to get the role for that company
      const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId')
      if (companyId) {
        try {
          // We need to fetch my companies to find the role for this specific company
          const myCompanies = await apiService.getMyCompanies();
          const currentCompanyAccess = myCompanies.find((c: any) => c.companyId === companyId || c.company?.id === companyId);
          if (currentCompanyAccess && currentCompanyAccess.role) {
            userRole = currentCompanyAccess.role;
            console.log(`[Login] Switched to company role: ${userRole} for company ${companyId}`);
          }
          
          // Also set access scope if available
          if (currentCompanyAccess && (currentCompanyAccess as any).accessScope) {
             accessScope = (currentCompanyAccess as any).accessScope;
          }
        } catch (e) {
          console.warn('Failed to fetch company specific role during login', e);
        }
      }
      const emailVerified = userProfile?.emailVerified ?? true // Default to true for backward compatibility
      const userPhone = userProfile?.phone || null
      const userCountry = (userProfile as any)?.country || null
      const userPassportNumber = (userProfile as any)?.passportNumber || null
      const userIdNumber = (userProfile as any)?.idNumber || null
      
      // Split name into firstName and lastName if available
      let firstName = "User"
      let lastName = ""
      if (userName) {
        const nameParts = userName.trim().split(' ')
        if (nameParts.length >= 2) {
          firstName = nameParts[0]
          lastName = nameParts.slice(1).join(' ')
        } else {
          firstName = nameParts[0]
        }
      }
      
      const userData = {
        id: payload.sub,
        email: userEmail,
        firstName,
        lastName,
        companyName,
        role: userRole as User["role"],
        phone: userPhone,
        country: userCountry,
        passportNumber: userPassportNumber,
        idNumber: userIdNumber,
        accessScope: accessScope || (userProfile as any)?.accessScope,
        isEmailVerified: emailVerified,

        createdAt: new Date().toISOString(),
        tenantId,
        // Also store the full name for easy access
        ...(userName && { fullName: userName, name: userName })
      }

      setUser(userData as User)

      // Redirect guest users to my-account, others to main dashboard
      if (userRole === 'guest') {
        navigate('/my-account')
      } else {
        navigate('/dashboard')
      }
    } catch (error) {
      console.error("❌ Login failed:", error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }


  // ---- Clear All Data ---- //
  const clearAllData = () => {
    if (typeof window === "undefined") return
    
    try {
      // Get all localStorage keys
      const keys = Object.keys(localStorage)
      
      // Remove all keys
      const keysToKeep: string[] = []
      
      keys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          try {
            localStorage.removeItem(key)
          } catch (error) {
            console.warn(`Failed to remove ${key} from localStorage:`, error)
          }
        }
      })
      
      console.log('All user data cleared from localStorage')
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
    }
  }

  // ---- Logout ---- //
  const logout = async () => {
    // Only run on client side
    if (typeof window === "undefined") {
      setUser(null)
      return
    }

    // Don't redirect if we're on a public auth page (like forgot password)
    const publicAuthPaths = ['/login', '/auth/login', '/auth/forgot-password', '/verify-email', '/unauthorized']
    const currentPath = window.location.pathname
    const isOnPublicAuthPage = publicAuthPaths.some(path => currentPath === path || currentPath.startsWith(path))

    try {
      const rt = localStorage.getItem("refresh_token")
      if (rt) await apiService.logout(rt)
    } catch (error) {
      console.error("Logout failed", error)
    } finally {
      // Clear all user data from localStorage
      clearAllData()
      
      // Clear API service auth
      apiService.setAuth("", "")
      setUser(null)
      
      // Clear query cache
      try { await qc.clear() } catch {}
      
      // Only navigate away if we're not on a public auth page
      if (!isOnPublicAuthPage) {
        try { navigate('/') } catch {}
      }
    }
  }

  // ---- Update User ---- //
  const updateUser = async (userData: Partial<User>) => {
    if (user) setUser({ ...user, ...userData })
  }

  // ---- useEffect hooks (after function definitions) ---- //
  
  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (mounted) {
      checkAuthStatus()
    }
  }, [mounted])

  // Listen for token expiration events from API service
  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return

    const handleTokenExpired = () => {
      console.warn("Token expired event received, logging out user")
      logout()
    }

    window.addEventListener('token-expired', handleTokenExpired)

    return () => {
      window.removeEventListener('token-expired', handleTokenExpired)
    }
  }, [mounted])

  // Set up periodic token refresh check
  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined" || !mounted) return

    if (!user) return

    const checkTokenExpiry = () => {
      const token = localStorage.getItem("auth_token")
      if (!token) {
        // No token, log out
        logout()
        return
      }

      const payload = parseJwt<JwtPayload>(token)
      if (!payload?.exp) {
        // Invalid token, log out
        logout()
        return
      }

      const now = Date.now() / 1000
      const timeUntilExpiry = payload.exp - now

      // If token is already expired, log out immediately
      if (timeUntilExpiry <= 0) {
        console.warn("Token has expired, logging out")
        logout()
        return
      }

      // If token expires in less than 10 minutes, refresh it
      if (timeUntilExpiry < 600) {
        refreshTokens().catch(() => {
          // refreshTokens will handle logout on failure
        })
      }
    }

    // Check every 2 minutes
    const interval = setInterval(checkTokenExpiry, 2 * 60 * 1000)
    
    // Also check immediately
    checkTokenExpiry()

    return () => clearInterval(interval)
  }, [user, mounted])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isRefreshing,
    login,
    logout,
    updateUser,
    refreshTokens,
    refreshUser: checkAuthStatus,
    ensureValidToken,
    handleAuthError,
    clearAllData,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ---- Hook ---- //
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    console.error('🔐 useAuth: Context is undefined - AuthProvider not found')
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
